import { Badge, Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaHistory } from "react-icons/fa";
import { ProcessStatusTimeline } from "../../models/Process";
import { getProcessStatusHistory } from "../../services/processService";
import { crm } from "../../theme";
import { formatDuration, parseBrt } from "../../utils/duration";
import SectionCard from "../../pages/process/components/SectionCard";
import StatusTimer from "./StatusTimer";

const fmtDateTime = (iso?: string) => {
  const ms = parseBrt(iso);
  if (ms === undefined) return "—";
  return new Date(ms).toLocaleString("pt-BR", { timeZone: "UTC", dateStyle: "short", timeStyle: "short" });
};

/** Seção "Linha do tempo de status" da tela do processo. */
const StatusTimelineSection = ({ processId }: { processId: string }) => {
  const [data, setData] = useState<ProcessStatusTimeline | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getProcessStatusHistory(processId).then((d) => {
      if (!alive) return;
      setData(d);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [processId]);

  const p2s = data?.productionToShipment;

  return (
    <SectionCard
      icon={<FaHistory />}
      title="Linha do tempo de status"
      subtitle={data?.rows?.length ? `${data.rows.length} ${data.rows.length === 1 ? "período" : "períodos"}` : undefined}
      actions={
        p2s ? (
          <Badge
            px="10px"
            py="4px"
            borderRadius="full"
            bg={p2s.running ? crm.brandSoft : crm.subtle}
            color={p2s.running ? crm.brand : crm.text}
            textTransform="none"
            fontWeight="600"
            title={`Da entrada em produção (${fmtDateTime(p2s.start)}) até o embarque`}
          >
            Produção → embarque: 
            {formatDuration(p2s.seconds)}
            {p2s.running ? " (em andamento)" : ""}
          </Badge>
        ) : undefined
      }
    >
      {loading ? (
        <Flex p="24px" justify="center">
          <Spinner size="sm" />
        </Flex>
      ) : !data?.rows?.length ? (
        <Text p="24px" color={crm.textMuted} fontSize="sm">
          Sem histórico de status registrado.
        </Text>
      ) : (
        <Box overflowX="auto">
          {data.rows.map((row, i) => (
            <Flex
              key={`${row.startedAt}-${i}`}
              align="center"
              gap="16px"
              px={{ base: "16px", md: "24px" }}
              py="12px"
              borderTop={i === 0 ? undefined : `1px solid ${crm.border}`}
              bg={row.isCurrent ? crm.brandSoft : undefined}
              wrap="wrap"
            >
              <Box flex="1" minW="180px">
                <Text fontSize="sm" fontWeight={row.isCurrent ? "700" : "500"} color={crm.text}>
                  {row.status}
                </Text>
                {(row.changedBy || row.source === "Backfill") && (
                  <Text fontSize="xs" color={crm.textFaint}>
                    {row.changedBy ? `por ${row.changedBy}` : "histórico reconstruído"}
                  </Text>
                )}
              </Box>
              <Text fontSize="xs" color={crm.textMuted} minW="120px">
                {fmtDateTime(row.startedAt)} → {row.endedAt ? fmtDateTime(row.endedAt) : "agora"}
              </Text>
              <Box minW="90px" textAlign="right">
                {row.isCurrent ? (
                  <StatusTimer since={row.startedAt} prefix="" fontWeight="700" color={crm.brand} fontSize="sm" />
                ) : (
                  <Text fontSize="sm" color={crm.text}>
                    {formatDuration(row.durationSeconds)}
                  </Text>
                )}
              </Box>
            </Flex>
          ))}
        </Box>
      )}
    </SectionCard>
  );
};

export default StatusTimelineSection;
